import { Abas } from '@core/components/Abas';
import { CancelButton, SaveButton } from '@core/components/Buttons';
import { FormDialogContent, SimpleDialog } from '@core/components/Dialog';
import { Grid } from '@material-ui/core';
import { FormikProvider, useFormik } from 'formik';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { FaArrowCircleDown } from 'react-icons/fa';
import * as yup from 'yup';
import { ContasPagarAction, useContasPagar } from '../store';
import CONTAS_PAGAR_SCHEMA from './ContasPagarSchema';
import { ContasPagarAbaAnexo, ContasPagarAbaBaixa, ContasPagarAbaDadosDespesa, ContasPagarAbaRetencao } from './abas';

const MOMENT_HOJE = moment();

const ContasPagarCadastro = () => {
  const [state, dispatch] = useContasPagar();
  const { registro, cadastrar, requestParams, initialAbaSelecionada, filtroAvancadoFormatado } = state;

  const [abaSelecionada, selecionarAba] = useState(initialAbaSelecionada);

  const onSubmitForm = (contasPagar) => {
    ContasPagarAction.onSubmit(dispatch, contasPagar, requestParams, filtroAvancadoFormatado);
  };

  const getInitialValues = (contaPagar) => ({
    ...contaPagar,
    categorias: contaPagar?.categorias ? contaPagar.categorias : [{ rateios: [{ classe: {}, percentual: '' }] }],
    competencia: contaPagar?.competencia ? contaPagar.competencia : MOMENT_HOJE,
    aproveitamentoCreditoAtivo: !!contaPagar?.aproveitamentoCredito?.id || false,
    aproveitamentoCredito: contaPagar?.aproveitamentoCredito ? contaPagar?.aproveitamentoCredito : null,
  });

  const formik = useFormik({
    initialValues: getInitialValues(registro),
    onSubmit: onSubmitForm,
    validateOnChange: true,
    validateOnBlur: true,
    validationSchema: () =>
      yup.lazy((values) => {
        switch (values.tipoDespesa) {
          case 'UNICA':
            return CONTAS_PAGAR_SCHEMA.UNICA;
          case 'LINHA_DIGITAVEL':
            return CONTAS_PAGAR_SCHEMA.LINHA_DIGITAVEL;
          case 'PARCELADA':
          case 'PARCELA':
            return CONTAS_PAGAR_SCHEMA.PARCELADA;

          default:
            return yup.mixed();
        }
      }),
  });

  const registroTipoDespesa = registro?.tipoDespesa;
  useEffect(() => {
    if (cadastrar) {
      formik.resetForm({
        values: getInitialValues(registro),
      });
      selecionarAba(initialAbaSelecionada);
    }
    // Reset controlado pelo gatilho acima; incluir o formulário/valores reiniciaria o
    // preenchimento enquanto o usuário edita.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cadastrar, registroTipoDespesa]);

  useEffect(() => {
    selecionarAba(initialAbaSelecionada);
  }, [initialAbaSelecionada]);

  const handleCancelar = () => {
    ContasPagarAction.fecharModalCadastro(dispatch, formik.dirty);
  };

  const dadosDespesaError = !!(
    formik.errors?.fornecedor ||
    formik.errors?.categorias?.length > 1 ||
    formik.errors?.categorias?.[0]?.valorBruto ||
    formik.errors?.dataVencimento
  );

  const isEdicao =
    (!!registro?.id && registro?.situacao === 'PARCIALMENTE_BAIXADA') ||
    (!!registro?.id && registro?.situacao === 'PARCIAL');
  const origemCompraProduto = formik?.values?.origemLancamento === 'COMPRA_PRODUTO';
  const origemCompra = origemCompraProduto || formik?.values?.origemLancamento === 'COMPRA_SERVICO';

  const valorBrutoPreenchido = formik.values?.categorias.some((c) => !!c.valorBruto);
  const categoriaPreenchida = formik.values?.categorias?.length > 0 ? !!formik.values.categorias[0].classe?.id : false;

  const desabilitarRetencoes =
    origemCompraProduto || formik.values?.categorias?.length > 1 || !valorBrutoPreenchido || !categoriaPreenchida;

  const retencoesError = !!(
    formik.errors?.retencoes?.[0]?.valor ||
    formik.errors?.retencoes?.[0]?.dataVencimento ||
    formik.errors?.retencoes?.[0]?.imposto
  );

  return (
    <SimpleDialog fullScreen open={cadastrar} onClose={handleCancelar} innerScroll>
      <FormikProvider value={formik}>
        {cadastrar && (
          <FormDialogContent
            onSubmit={formik.handleSubmit}
            fullScreen
            header={{
              title: 'Lançamento de contas a pagar',
              icon: <FaArrowCircleDown />,
              toolbar: (
                <>
                  <CancelButton title="cancelar" type="button" onClick={handleCancelar}>
                    Cancelar
                  </CancelButton>
                  <SaveButton color="buttonSave" type="submit">
                    Salvar
                  </SaveButton>
                </>
              ),
            }}
            contentToolbar={
              <Abas
                value={abaSelecionada}
                onChange={selecionarAba}
                tabs={[
                  {
                    label: 'Dados da despesa',
                    hasError: () => dadosDespesaError,
                  },
                  {
                    label: 'Retenções',
                    hasError: () => retencoesError,
                    disabled: desabilitarRetencoes,
                  },
                  {
                    label: 'Anexar documento',
                  },
                  {
                    label:
                      formik?.values?.tipoDespesa === 'PARCELADA' ? 'Baixa da 1º parcela' : 'Baixa da conta principal',
                  },
                ]}
              />
            }
            content={
              <div className="h-full max-h-full w-full">
                <Grid container spacing={3}>
                  {abaSelecionada === 0 && (
                    <ContasPagarAbaDadosDespesa isEdicao={isEdicao} origemCompra={origemCompra} />
                  )}
                  {abaSelecionada === 1 && <ContasPagarAbaRetencao isEdicao={isEdicao} origemCompra={origemCompra} />}
                  {abaSelecionada === 2 && <ContasPagarAbaAnexo />}
                  {abaSelecionada === 3 && <ContasPagarAbaBaixa origemCompra={origemCompra} />}
                </Grid>
              </div>
            }
          />
        )}
      </FormikProvider>
    </SimpleDialog>
  );
};

export default ContasPagarCadastro;
